import { Check, X as XIcon } from 'lucide-react';
import type { PasswordPolicy } from '../../services/auth.service';

interface Props {
  policy: PasswordPolicy | null;
  password: string;
  email?: string;
}

interface Check {
  label: string;
  satisfied: boolean;
}

/**
 * Evaluate the password against the Keycloak realm policy and render a live
 * checklist. Mirrors the validation KC enforces server-side so users can see
 * exactly what's missing before submit.
 */
function buildChecks(policy: PasswordPolicy, password: string, email?: string): Check[] {
  const checks: Check[] = [];

  if (policy.minLength && policy.minLength > 0) {
    checks.push({
      label: `At least ${policy.minLength} characters`,
      satisfied: password.length >= policy.minLength,
    });
  }

  if (policy.maxLength && policy.maxLength > 0) {
    checks.push({
      label: `At most ${policy.maxLength} characters`,
      satisfied: password.length <= policy.maxLength,
    });
  }

  if (policy.upperCase && policy.upperCase > 0) {
    const count = (password.match(/[A-Z]/g) || []).length;
    checks.push({
      label: policy.upperCase === 1
        ? 'At least 1 uppercase letter'
        : `At least ${policy.upperCase} uppercase letters`,
      satisfied: count >= policy.upperCase,
    });
  }

  if (policy.lowerCase && policy.lowerCase > 0) {
    const count = (password.match(/[a-z]/g) || []).length;
    checks.push({
      label: policy.lowerCase === 1
        ? 'At least 1 lowercase letter'
        : `At least ${policy.lowerCase} lowercase letters`,
      satisfied: count >= policy.lowerCase,
    });
  }

  if (policy.digits && policy.digits > 0) {
    const count = (password.match(/\d/g) || []).length;
    checks.push({
      label: policy.digits === 1 ? 'At least 1 digit' : `At least ${policy.digits} digits`,
      satisfied: count >= policy.digits,
    });
  }

  if (policy.specialChars && policy.specialChars > 0) {
    const count = (password.match(/[^A-Za-z0-9]/g) || []).length;
    checks.push({
      label: policy.specialChars === 1
        ? 'At least 1 special character'
        : `At least ${policy.specialChars} special characters`,
      satisfied: count >= policy.specialChars,
    });
  }

  if (policy.notEmail && email) {
    checks.push({
      label: "Must not contain your email",
      satisfied: !password.toLowerCase().includes(email.toLowerCase()),
    });
  }

  return checks;
}

export function validatePassword(policy: PasswordPolicy, password: string, email?: string): string | null {
  const checks = buildChecks(policy, password, email);
  const failing = checks.find((c) => !c.satisfied);
  return failing ? failing.label : null;
}

export default function PasswordPolicyHints({ policy, password, email }: Props) {
  if (!policy) return null;
  const checks = buildChecks(policy, password, email);
  if (checks.length === 0) return null;

  return (
    <ul className="mt-2 space-y-1 text-xs text-gray-600" aria-live="polite">
      {checks.map((c) => (
        <li key={c.label} className={`flex items-center gap-2 ${c.satisfied ? 'text-emerald-600' : 'text-gray-500'}`}>
          {c.satisfied ? (
            <Check className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          ) : (
            <XIcon className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" />
          )}
          <span>{c.label}</span>
        </li>
      ))}
    </ul>
  );
}
